/**
 * Nebula / star / Droste pass into a half-resolution RGBA8 target. Rendered every frame on tier 2 and every
 * second frame on tiers 0–1 (the caller decides); in calm mode the caller stops advancing its time.
 */
import { createProgram, createTarget, destroyTarget, type FullscreenQuad, type Program, type Target } from './gl';
import type { RGB } from './palette';
import { NEBULA_FRAG } from './shaders/nebula';
import { QUAD_VERT } from './shaders/quad';

export interface NebulaParams {
  cssWidth: number; cssHeight: number;
  cx: number; cy: number; r0: number; R: number;
  timeSec: number; droste: number; contrast: number; theme: number; tint: RGB;
}

export class NebulaPass {
  private readonly gl: WebGL2RenderingContext;
  private readonly quad: FullscreenQuad;
  private readonly prog: Program;
  private target: Target | null = null;

  constructor(gl: WebGL2RenderingContext, quad: FullscreenQuad) {
    this.gl = gl;
    this.quad = quad;
    this.prog = createProgram(gl, QUAD_VERT, NEBULA_FRAG);
  }

  get texture(): WebGLTexture | null { return this.target?.textures[0] ?? null; }

  resize(devWidth: number, devHeight: number): void {
    const gl = this.gl;
    const w = Math.max(1, Math.round(devWidth / 2));
    const h = Math.max(1, Math.round(devHeight / 2));
    if (this.target && this.target.width === w && this.target.height === h) return;
    destroyTarget(gl, this.target);
    this.target = createTarget(gl, w, h, { internalFormat: gl.RGBA8, format: gl.RGBA, type: gl.UNSIGNED_BYTE, filter: gl.LINEAR });
  }

  render(p: NebulaParams): void {
    const gl = this.gl;
    const t = this.target;
    if (!t) return;
    gl.bindFramebuffer(gl.FRAMEBUFFER, t.fb);
    gl.viewport(0, 0, t.width, t.height);
    this.prog.use();
    gl.uniform2f(this.prog.loc('uCanvasCss'), p.cssWidth, p.cssHeight);
    gl.uniform2f(this.prog.loc('uCentre'), p.cx, p.cy);
    gl.uniform1f(this.prog.loc('uR0'), p.r0);
    gl.uniform1f(this.prog.loc('uR'), p.R);
    gl.uniform1f(this.prog.loc('uTime'), p.timeSec);
    gl.uniform1f(this.prog.loc('uDroste'), p.droste);
    gl.uniform1f(this.prog.loc('uContrast'), p.contrast);
    gl.uniform1f(this.prog.loc('uTheme'), p.theme);
    gl.uniform3f(this.prog.loc('uTint'), p.tint[0], p.tint[1], p.tint[2]);
    this.quad.draw();
  }

  destroy(): void {
    destroyTarget(this.gl, this.target);
    this.target = null;
    this.prog.destroy();
  }
}
